import { JOBS_BY_ID } from '@renavora/content';
import {
  hasFreePartySlotForCap,
  partyLinkCapForTier,
  validatePartyFormationByCap,
  type PartyLink,
  type PartyLinkFormationResult,
  type PartySlotState,
} from '@renavora/shared';

// Client-side party-link surface (ADR 0038 §2.2). The pure, content-free
// invariants now live in `@renavora/shared` (`party-link.ts`) so the server
// reuses the identical logic (substep 2). This module is a thin wrapper that
// (a) re-exports the shared pure helpers unchanged and (b) keeps the
// content-aware, jobId-based entry points the store/UI/local-mock already use —
// resolving the slot cap from the live job tier (`JOBS_BY_ID`) before
// delegating to the shared cap-based core. Existing imports stay byte-identical.

export {
  PartyRequestTransitionError,
  addPartyLink,
  approvePartyRequest,
  createPartyRequest,
  declinePartyRequest,
  formPartyLinks,
  partnerToBorrowedAlly,
  partyLinksToBorrowedAllies,
  refreshPartyLink,
  removePartyLinkById,
  type PartyLinkFormationError,
  type PartyLinkFormationOk,
  type PartyLinkFormationReason,
  type PartyLinkFormationResult,
  type PartySlotState,
} from '@renavora/shared';

/** One side's current linking context: identity + job + current links. The cap
 * is resolved from `jobId` via the content job catalogue. */
export interface PartySlotContext {
  readonly accountId: string;
  readonly characterId: string;
  readonly jobId: string;
  readonly links: readonly PartyLink[];
}

/** Mutual-party slot cap for a job's tier (0 when the job is unresolved). */
export function partyLinkCapForJob(jobId: string): number {
  const tier = JOBS_BY_ID.get(jobId)?.tier ?? null;
  if (tier === null) return 0;
  return partyLinkCapForTier(tier);
}

/** Whether a character at `jobId` holding `linkCount` links has an open slot. */
export function hasFreePartyLinkSlot(jobId: string, linkCount: number): boolean {
  return hasFreePartySlotForCap(linkCount, partyLinkCapForJob(jobId));
}

function toSlotState(context: PartySlotContext): PartySlotState {
  return {
    accountId: context.accountId,
    characterId: context.characterId,
    cap: partyLinkCapForJob(context.jobId),
    links: context.links,
  };
}

/**
 * Validate forming a mutual link between `local` and `partner`. Resolves each
 * side's cap from its job tier, then delegates to the shared cap-based core.
 * Keeps the original argument semantics: a full `local` surfaces as
 * `requester_slot_full`, a full `partner` as `addressee_slot_full`.
 */
export function validatePartyLinkFormation(
  local: PartySlotContext,
  partner: PartySlotContext,
): PartyLinkFormationResult {
  return validatePartyFormationByCap(toSlotState(local), toSlotState(partner));
}
